<script setup lang="ts">
/**
 * 源站 AppFloatButtons：canvas 逐帧 + EBe() 槽位。
 * 锚点用 .app-frame（源站 #app）；滚动 is-scroll-hidden 420ms。
 * 拖拽抄 DraggableSpriteFloatBtn：6px 阈值、夹紧、resize 吸右缘。
 * jackpot 72@24fps 老虎机视窗字母轮 / scratch 72@24fps / sign-in 72@24fps / wheel 45@20fps。
 * 帧图合成为 4 张 WebP 雪碧图；首屏只画 poster，页面 load + idle 后才拉雪碧图，
 * 按钮滚动隐藏 / 页面不可见时停 rAF。
 */
import { onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import {
  oroMxAssetPaths,
  oroMxFloatSheet,
  oroMxFloatSheetOffset,
  type OroMxFloatSheet,
} from '@/shared/theme/oro-mx-home-data';
import {
  clampOroMxFloatPosition,
  computeOroMxFloatLayout,
  computeOroMxRootFont,
  resolveOroMxFloatAnchor,
} from '@/shared/theme/oro-mx-rem';
import { useAuthModalStore } from '@/stores/auth-modal-store';
import { useOroMxShellStore } from '@/stores/oro-mx-shell-store';
import { useSessionStore } from '@/stores/session-store';

const router = useRouter();
const authModal = useAuthModalStore();
const sessionStore = useSessionStore();
const shell = useOroMxShellStore();

type FloatId = 'jackpot' | 'scratch' | 'sign' | 'wheel';

const items: { fps: number; id: FloatId; label: string; poster: string; sheet: OroMxFloatSheet }[] = [
  {
    id: 'jackpot',
    label: 'jackpot',
    fps: 24,
    poster: oroMxAssetPaths.floatJackpot,
    sheet: oroMxFloatSheet('jackpot-window', 72),
  },
  {
    id: 'scratch',
    label: 'scratch',
    fps: 24,
    poster: oroMxAssetPaths.floatScratch,
    sheet: oroMxFloatSheet('scratch', 72),
  },
  {
    id: 'sign',
    label: 'sign-in',
    fps: 24,
    poster: oroMxAssetPaths.floatSignIn,
    sheet: oroMxFloatSheet('sign-in', 72),
  },
  {
    id: 'wheel',
    label: 'wheel',
    fps: 20,
    poster: oroMxAssetPaths.floatWheel,
    sheet: oroMxFloatSheet('wheel', 45),
  },
];

const layout = ref(computeOroMxFloatLayout({ top: 0, right: 450, bottom: 900 }, 22.5));
const pos = reactive<Record<FloatId, { x: number; y: number }>>({
  jackpot: { x: 378, y: 372 },
  scratch: { x: 378, y: 451.2 },
  sign: { x: 378, y: 530.4 },
  wheel: { x: 378, y: 609.6 },
});
const scrollHidden = ref(false);
const canvasRefs = new Map<FloatId, HTMLCanvasElement>();
const sheets = new Map<FloatId, HTMLImageElement>();
const posters = new Map<FloatId, HTMLImageElement>();
let raf = 0;
let startMs = 0;
let sheetsRequested = false;
let unmounted = false;
let hideTimer = 0;
const SCROLL_HIDE_MS = 420;
const DRAG_THRESHOLD = 6;

let draggingId: FloatId | null = null;
let dragMoved = false;
let dragPointerId: number | null = null;
let dragBtn: HTMLElement | null = null;
let dragStartClientX = 0;
let dragStartClientY = 0;
let dragStartX = 0;
let dragStartY = 0;

function setCanvas(id: FloatId, el: HTMLCanvasElement | null) {
  if (el) canvasRefs.set(id, el);
  else canvasRefs.delete(id);
}

function currentAnchor() {
  const frame = document.querySelector<HTMLElement>('.app-frame.skin-oro-mx')
    ?? document.querySelector<HTMLElement>('.app-frame');
  const frameRect = frame?.getBoundingClientRect();
  return resolveOroMxFloatAnchor(
    frameRect
      ? {
          left: frameRect.left,
          top: frameRect.top,
          right: frameRect.right,
          bottom: frameRect.bottom,
        }
      : null,
    { width: window.innerWidth, height: window.innerHeight },
  );
}

function currentRootFont() {
  return computeOroMxRootFont(document.documentElement.clientWidth || window.innerWidth);
}

function snapToSlots() {
  const rect = currentAnchor();
  const rootFont = currentRootFont();
  layout.value = computeOroMxFloatLayout(rect, rootFont);
  for (const item of items) {
    const next = clampOroMxFloatPosition(layout.value[item.id].x, layout.value[item.id].y, rect, rootFont);
    pos[item.id].x = next.x;
    pos[item.id].y = next.y;
  }
}

/** 源站 L()：拖着时不改；否则 X 吸右缘，Y 保持。 */
function onResizeSnap() {
  if (draggingId) return;
  const rect = currentAnchor();
  const rootFont = currentRootFont();
  layout.value = computeOroMxFloatLayout(rect, rootFont);
  for (const item of items) {
    const next = clampOroMxFloatPosition(layout.value[item.id].x, pos[item.id].y, rect, rootFont);
    pos[item.id].x = next.x;
    pos[item.id].y = next.y;
  }
}

function onPointerMove(ev: PointerEvent) {
  if (!draggingId || ev.pointerId !== dragPointerId) return;
  const dx = ev.clientX - dragStartClientX;
  const dy = ev.clientY - dragStartClientY;
  if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) dragMoved = true;
  const next = clampOroMxFloatPosition(
    dragStartX + dx,
    dragStartY + dy,
    currentAnchor(),
    currentRootFont(),
  );
  pos[draggingId].x = next.x;
  pos[draggingId].y = next.y;
}

function stopDragListeners() {
  window.removeEventListener('pointermove', onPointerMove);
  window.removeEventListener('pointerup', onPointerUp);
  window.removeEventListener('pointercancel', onPointerUp);
}

function onPointerUp(ev: PointerEvent) {
  if (!draggingId || ev.pointerId !== dragPointerId) return;
  const id = draggingId;
  const moved = dragMoved;
  draggingId = null;
  dragPointerId = null;
  try {
    if (dragBtn?.hasPointerCapture(ev.pointerId)) dragBtn.releasePointerCapture(ev.pointerId);
  } catch {
    // 非可信 PointerEvent / 已释放
  }
  dragBtn = null;
  stopDragListeners();
  if (!moved) onClick(id);
}

function onPointerDown(id: FloatId, ev: PointerEvent) {
  if (scrollHidden.value) return;
  if (ev.button !== undefined && ev.button !== 0) return;
  ev.preventDefault();
  draggingId = id;
  dragMoved = false;
  dragStartClientX = ev.clientX;
  dragStartClientY = ev.clientY;
  dragStartX = pos[id].x;
  dragStartY = pos[id].y;
  dragPointerId = ev.pointerId;
  dragBtn = ev.currentTarget instanceof HTMLElement ? ev.currentTarget : null;
  try {
    dragBtn?.setPointerCapture(ev.pointerId);
  } catch {
    // 非可信 PointerEvent 仍走 window 监听
  }
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
  window.addEventListener('pointercancel', onPointerUp);
}

function clearHideTimer() {
  if (!hideTimer) return;
  window.clearTimeout(hideTimer);
  hideTimer = 0;
}

function onScrollHide() {
  scrollHidden.value = true;
  clearHideTimer();
  hideTimer = window.setTimeout(() => {
    scrollHidden.value = false;
    hideTimer = 0;
  }, SCROLL_HIDE_MS);
}

/** 雪碧图共 4 张 WebP（约 1.9 MB），等首屏 load 完再延后拉，不与 LCP/banner 抢带宽。 */
const SHEET_START_DELAY_MS = 2500;
/** load 迟迟不来（某张图挂起）时的兜底，保证动画最终会启动。 */
const SHEET_START_MAX_WAIT_MS = 8000;
let sheetStartTimer = 0;
let sheetStartFallback = 0;
let sheetStartBound = false;

function isReady(img: HTMLImageElement | undefined): img is HTMLImageElement {
  return Boolean(img?.complete && img.naturalWidth > 0);
}

function cancelSheetStart() {
  if (sheetStartTimer) {
    window.clearTimeout(sheetStartTimer);
    sheetStartTimer = 0;
  }
  if (sheetStartFallback) {
    window.clearTimeout(sheetStartFallback);
    sheetStartFallback = 0;
  }
  if (sheetStartBound) {
    window.removeEventListener('load', onWindowLoadStartSheets);
    sheetStartBound = false;
  }
}

function loadSheets() {
  if (sheetsRequested || unmounted) return;
  sheetsRequested = true;
  cancelSheetStart();
  for (const item of items) {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      sheets.set(item.id, img);
      ensureLoop();
    };
    img.src = item.sheet.src;
  }
}

function onWindowLoadStartSheets() {
  sheetStartBound = false;
  if (sheetStartTimer) return;
  sheetStartTimer = window.setTimeout(() => {
    sheetStartTimer = 0;
    loadSheets();
  }, SHEET_START_DELAY_MS);
}

function scheduleSheetStart() {
  if (document.readyState === 'complete') {
    onWindowLoadStartSheets();
    return;
  }
  sheetStartBound = true;
  window.addEventListener('load', onWindowLoadStartSheets, { once: true });
  sheetStartFallback = window.setTimeout(loadSheets, SHEET_START_MAX_WAIT_MS);
}

/** poster 只画一次；雪碧图到位前 canvas 一直停在 poster 上。 */
function paintPoster(id: FloatId) {
  if (sheets.has(id)) return;
  const canvas = canvasRefs.get(id);
  const poster = posters.get(id);
  if (!canvas || !isReady(poster)) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(poster, 0, 0, canvas.width, canvas.height);
}

function loadPosters() {
  for (const item of items) {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => paintPoster(item.id);
    img.src = item.poster;
    posters.set(item.id, img);
  }
}

function shouldAnimate() {
  return sheets.size > 0 && !scrollHidden.value && !document.hidden;
}

function ensureLoop() {
  if (raf || unmounted || !shouldAnimate()) return;
  raf = window.requestAnimationFrame(draw);
}

function stopLoop() {
  if (!raf) return;
  window.cancelAnimationFrame(raf);
  raf = 0;
}

/** 上次画的帧号，没变就不重绘。 */
const drawnIndex = new Map<FloatId, number>();

function draw(now: number) {
  raf = 0;
  if (unmounted || !shouldAnimate()) return;
  if (!startMs) startMs = now;
  const elapsed = now - startMs;
  for (const item of items) {
    const canvas = canvasRefs.get(item.id);
    const sheet = sheets.get(item.id);
    if (!canvas || !isReady(sheet)) continue;
    const index = Math.floor((elapsed / 1000) * item.fps) % item.sheet.count;
    if (drawnIndex.get(item.id) === index) continue;
    const ctx = canvas.getContext('2d');
    if (!ctx) continue;
    const { x, y } = oroMxFloatSheetOffset(item.sheet, index);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(sheet, x, y, item.sheet.frame, item.sheet.frame, 0, 0, canvas.width, canvas.height);
    drawnIndex.set(item.id, index);
  }
  raf = window.requestAnimationFrame(draw);
}

function onVisibilityChange() {
  if (document.hidden) stopLoop();
  else ensureLoop();
}

watch(scrollHidden, (hidden) => {
  if (hidden) stopLoop();
  else ensureLoop();
});

onMounted(() => {
  snapToSlots();
  window.addEventListener('resize', onResizeSnap, { passive: true });
  document.addEventListener('scroll', onScrollHide, { capture: true, passive: true });
  document.addEventListener('visibilitychange', onVisibilityChange);
  loadPosters();
  scheduleSheetStart();
});

onUnmounted(() => {
  unmounted = true;
  window.removeEventListener('resize', onResizeSnap);
  document.removeEventListener('scroll', onScrollHide, { capture: true });
  document.removeEventListener('visibilitychange', onVisibilityChange);
  stopDragListeners();
  clearHideTimer();
  cancelSheetStart();
  stopLoop();
});

function onClick(id: FloatId) {
  if (id === 'jackpot') {
    shell.openJackpot();
    return;
  }
  if (id === 'sign') {
    void router.push('/sign-in');
    return;
  }
  if (id === 'wheel') {
    if (!sessionStore.isAuthenticated) {
      authModal.openAuth({ mode: 'login' });
    }
    void router.push('/lottery-wheel');
    return;
  }
  // 源站 Sw() → wufuModal.open：未登录先弹登录。
  if (!sessionStore.isAuthenticated) {
    authModal.openAuth({ mode: 'login' });
    return;
  }
  shell.openScratch();
}
</script>

<template>
  <div class="oro-mx-floats">
    <button
      v-for="item in items"
      :key="item.id"
      class="draggable-sprite-float-btn"
      :class="{ 'is-scroll-hidden': scrollHidden }"
      type="button"
      :aria-label="item.label"
      :style="{
        left: `${pos[item.id].x}px`,
        top: `${pos[item.id].y}px`,
        zIndex: layout[item.id].z,
      }"
      @pointerdown="onPointerDown(item.id, $event)"
    >
      <canvas
        :ref="(el) => setCanvas(item.id, el as HTMLCanvasElement | null)"
        class="draggable-sprite-float-btn__canvas"
        width="144"
        height="144"
        aria-hidden="true"
      />
    </button>
  </div>
</template>

<style scoped>
.oro-mx-floats {
  display: contents;
}

.draggable-sprite-float-btn {
  position: fixed;
  display: grid;
  place-items: center;
  width: 3.2rem;
  height: 3.2rem;
  padding: 0;
  background: transparent;
  border: 0;
  cursor: grab;
  touch-action: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition:
    transform 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.22s ease;
  will-change: transform, opacity;
}

.draggable-sprite-float-btn:active {
  cursor: grabbing;
}

.draggable-sprite-float-btn.is-scroll-hidden {
  transform: translate(calc(100% + 0.74667rem));
  opacity: 0;
  pointer-events: none;
}

.draggable-sprite-float-btn__canvas {
  display: block;
  width: 3.2rem;
  height: 3.2rem;
  max-width: 3.2rem;
  max-height: 3.2rem;
  pointer-events: none;
}
</style>
